import { ApplicationRef, Injectable, Injector } from '@angular/core';
import { environment } from '@env';
import {
  doctorSpecializationRegistry,
  IDoctorSpecializationEntry,
  parseDoctorSpecializationEntry,
  TDoctorSpecializationLocaleKey,
} from '../view-models/consts/doctor-specialization-registry';
import { LocaleService } from '../translate/locale-getter';
import { HcRemoteConfigService } from './hc-remote-config.service';

const CACHE_STORAGE_KEY = 'doctorSpecializationsCache';

@Injectable({
  providedIn: 'root',
})
export class DoctorSpecializationRegistryService {
  constructor(
    private hcRemoteConfigService: HcRemoteConfigService,
    private localeService: LocaleService,
    private injector: Injector,
  ) {}

  // Loads the last valid list from the local cache and keeps a realtime Firestore listener open, mirroring
  // HcService.initialiseRemoteConfig. Fallback order: Firestore -> cache -> local enum/hardcoded ids
  // (the registry stays empty and the consumers use the local lists). Never rejects, so it can't block bootstrap.
  public async initialise(): Promise<void> {
    try {
      doctorSpecializationRegistry.setLocaleKeyResolver(() => this.getLocaleKey());

      const cachedEntries = this.readCache();

      if (cachedEntries.length) {
        doctorSpecializationRegistry.setEntries(cachedEntries);
      }

      if ((environment as { disableRemoteHcConfig?: boolean }).disableRemoteHcConfig) {
        return;
      }

      let isFirstUpdate = true;
      const firstLoad = this.hcRemoteConfigService.listenToSpecialties((entries) => {
        doctorSpecializationRegistry.setEntries(entries);
        this.writeCache(entries);

        if (isFirstUpdate) {
          isFirstUpdate = false;
          return;
        }

        // Refresh default change detection views showing a specialization name. ApplicationRef is resolved
        // lazily to avoid a cyclic DI dependency, since this service is used by an APP_INITIALIZER.
        this.injector.get(ApplicationRef).tick();
      });

      // With a cached list there is nothing to wait for; the listener applies updates whenever they arrive.
      if (!cachedEntries.length) {
        await firstLoad;
      }
    } catch (error) {
      console.error('[DoctorSpecializationRegistryService] Failed to initialise specializations:', error);
    }
  }

  private getLocaleKey(): TDoctorSpecializationLocaleKey {
    const locale = String(this.localeService.getCurrentLocale()).toLowerCase();

    return locale === 'en' || locale === 'es' ? locale : 'pt-BR';
  }

  private readCache(): IDoctorSpecializationEntry[] {
    try {
      const parsed = JSON.parse(localStorage.getItem(CACHE_STORAGE_KEY));

      return Array.isArray(parsed)
        ? parsed
            .map((item) => parseDoctorSpecializationEntry(item))
            .filter((entry): entry is IDoctorSpecializationEntry => !!entry)
        : [];
    } catch {
      return [];
    }
  }

  private writeCache(entries: IDoctorSpecializationEntry[]): void {
    try {
      localStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(entries));
    } catch {
      // Storage unavailable or full: the in-memory list keeps working, only the offline cache is lost.
    }
  }
}
